import {useContext, useEffect, useMemo, useState} from 'react';

import {UserContext} from '../../../contexts/UserContext/UserContext';
import {InboxFilters} from '../../../features/company/recruiting/inbox/types/InboxTypes';
import {usePagination} from '../../../hooks/usePagination';
import {MatchStatus} from '../../../types/MatchStatus/MatchStatus';
import {MatchData} from '../../../types/shared-recruiting-types';
import {CompanyAdminRoleData} from '../../../types/User';

export const useInboxPage = () => {
  const {currentUser} = useContext(UserContext);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  const companyId = useMemo(() => {
    return (currentUser?.role?.data as CompanyAdminRoleData)?.companyId;
  }, [currentUser]);

  const {
    fetchNext: fetchNewInbox,
    isLoading: isLoadingNewInbox,
    pagination: paginationNewInbox,
    error: errorNewInbox,
    data: newInbox,
    initializeData: initializeDataNewInbox,
    hasMore: hasMoreNewInbox,
    totalCountPerStatus,
    updateMatchTags: updateMatchTagsNewInbox,
  } = usePagination();

  const {
    fetchNext: fetchContactedInbox,
    isLoading: isLoadingContactedInbox,
    pagination: paginationContactedInbox,
    error: errorContactedInbox,
    data: contacted,
    initializeData: initializeDataContactedInbox,
    hasMore: hasMoreContactedInbox,
    updateMatchTags: updateMatchTagsContactedInbox,
  } = usePagination();

  const {
    fetchNext: fetchInterviewedInbox,
    isLoading: isLoadingInterviewedInbox,
    pagination: paginationInterviewedInbox,
    error: errorInterviewedInbox,
    data: interview,
    initializeData: initializeDataInterviewedInbox,
    hasMore: hasMoreInterviewedInbox,
    updateMatchTags: updateMatchTagsInterviewedInbox,
  } = usePagination();

  const initializeData = (filtersParams?: InboxFilters) => {
    initializeDataNewInbox(`api/v1/companies/${companyId}/matches?status=${MatchStatus.Accepted}`, filtersParams);

    initializeDataContactedInbox(
      `api/v1/companies/${companyId}/matches?status=${MatchStatus.Contacted}`,
      filtersParams
    );

    initializeDataInterviewedInbox(
      `api/v1/companies/${companyId}/matches?status=${MatchStatus.Interview}`,
      filtersParams
    );
  };

  useEffect(() => {
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, initializeDataNewInbox, initializeDataContactedInbox, initializeDataInterviewedInbox]);

  const fetchNextByStatus = (status: string, filtersParams?: InboxFilters) => {
    if (
      status === MatchStatus.Accepted &&
      !hasMoreByStatus(MatchStatus.Accepted) &&
      paginationNewInbox.nextPage === null
    )
      return;

    if (
      status === MatchStatus.Contacted &&
      !hasMoreByStatus(MatchStatus.Contacted) &&
      paginationContactedInbox.nextPage === null
    )
      return;

    if (
      status === MatchStatus.Interview &&
      !hasMoreByStatus(MatchStatus.Interview) &&
      paginationInterviewedInbox.nextPage === null
    )
      return;

    if (isLoadingNewInbox || isLoadingContactedInbox || isLoadingInterviewedInbox) return;

    if (status === MatchStatus.Accepted) {
      fetchNewInbox(`api/v1/companies/${companyId}/matches?status=${MatchStatus.Accepted}`, filtersParams);
    }
    if (status === MatchStatus.Contacted) {
      fetchContactedInbox(`api/v1/companies/${companyId}/matches?status=${MatchStatus.Contacted}`, filtersParams);
    }
    if (status === MatchStatus.Interview) {
      fetchInterviewedInbox(`api/v1/companies/${companyId}/matches?status=${MatchStatus.Interview}`, filtersParams);
    }
  };

  const hasMoreByStatus = (status: string) => {
    if (status === MatchStatus.Accepted) {
      return hasMoreNewInbox;
    }
    if (status === MatchStatus.Contacted) {
      return hasMoreContactedInbox;
    }
    if (status === MatchStatus.Interview) {
      return hasMoreInterviewedInbox;
    }
    return false;
  };

  const updateMatchTags = (status: string, match: MatchData) => {
    if (status === MatchStatus.Accepted) {
      updateMatchTagsNewInbox(match);
    }
    if (status === MatchStatus.Contacted) {
      updateMatchTagsContactedInbox(match);
    }

    if (status === MatchStatus.Interview) {
      updateMatchTagsInterviewedInbox(match);
    }
  };

  useEffect(() => {
    if (!isLoadingNewInbox && !isLoadingContactedInbox && !isLoadingInterviewedInbox) {
      setInitialLoad(false);
    }
  }, [isLoadingNewInbox, isLoadingContactedInbox, isLoadingInterviewedInbox]);

  return {
    fetchNextByStatus,
    initialLoad,
    isLoading: isLoadingNewInbox || isLoadingContactedInbox || isLoadingInterviewedInbox,
    error: errorNewInbox || errorContactedInbox || errorInterviewedInbox,
    newInbox,
    contacted,
    interview,
    hasMoreByStatus,
    totalCountPerStatus,
    initializeData,
    updateMatchTags,
  };
};
